import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';

import { useGetRolesQuery, useSignUpMutation } from 'api/main';
import { tunnelSelectors, userSelectors } from 'reducers';
import { initCompanyPayload } from 'schemas/tunnel';
import { isStatusOk, validateSchema } from 'utils/helpers';
import { useGetReferralParameters, useTunnelChangeHandlers } from 'utils/hooks';
import { corporateForms } from 'utils/formData';
import { texts } from 'utils/languages/fr';
import {
    userAvailabilityStatus,
    EMAIL_VALIDATION_OPTIONS,
    InternalErrors,
    PASS_REGEX,
    NAME_REGEX,
    stepStatus,
} from 'utils/constants';

import PasswordForm from './PasswordForm';
import BasicInfoForm from './BasicInfoForm';
import { TunnelBox } from 'containers/tunnel/common';
import ConditionsForm from '../contact-info/forms/ConditionsForm';
import { CustomSelect, ErrorMessage, CustomTextField, SubmitButton } from 'components';
import { MenuItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    field: { width: '100%', maxWidth: theme.constants.tunnelInputWidth },
    indentedField: { paddingLeft: 20 },
}));

const PMForm = props => {
    const { userStep, roleName, tunnelStep, handleNextStep } = props;
    const classes = useStyles();

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const { subcategory, product } = useGetReferralParameters();
    const { data: roles = [] } = useGetRolesQuery();
    const [signUp, { isLoading, isSuccess, isError, error, reset }] = useSignUpMutation({
        fixedCacheKey: 'register',
    });

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const tunnelErrors = useSelector(tunnelSelectors.getTunnelErrors);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);

    const {
        label,
        email: queryEmail,
        externalDistributorEmail,
        firstName: queryFirstName,
        lastName: queryLastName,
        title: queryCivility,
    } = useGetReferralParameters();

    const defaultForm = useMemo(
        () =>
            initCompanyPayload(
                isCreatingSubscription ? null : internalUser,
                tunnelSubscription,
                queryEmail,
                queryFirstName,
                queryLastName,
                queryCivility
            ),
        [
            isCreatingSubscription,
            internalUser,
            tunnelSubscription,
            queryEmail,
            queryFirstName,
            queryLastName,
            queryCivility,
        ]
    );
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        otherActivity: '',
        otherCorporateForm: '',
    });
    const roleId = useMemo(() => roles.find(r => r.name === roleName)?.id, [roles, roleName]);
    const fundId = useMemo(
        () => product || currentInvestmentFund?.id,
        [product, currentInvestmentFund]
    );

    useEffect(() => setForm(defaultForm), [defaultForm]);

    const [emailExists, setEmailExists] = useState(false);
    const isResubmit = useMemo(
        () => userStep?.successfulAttemptsNumber > 0 || userStep?.status === stepStatus.INCOMPLETE,
        [userStep]
    );
    const isAccountNotConfirmed = useMemo(
        () => internalUser?.availabilityStatus === userAvailabilityStatus.PENDING_PASSWORD_CHANGE,
        [internalUser]
    );
    const isStatusAcceptable = useMemo(() => isStatusOk(userStep), [userStep]);

    const schema = useMemo(
        () => ({
            firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
                'string.min': texts.FIRST_NAME_LENGTH,
                'string.max': texts.FIRST_NAME_LENGTH,
                'string.pattern.base': texts.FIRST_NAME_PATTERN,
            }),
            lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
                'string.min': texts.LAST_NAME_LENGTH,
                'string.max': texts.LAST_NAME_LENGTH,
                'string.pattern.base': texts.LAST_NAME_PATTERN,
            }),
            birthName: Joi.string().trim().max(50).pattern(NAME_REGEX).allow('').messages({
                'string.max': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
                'string.pattern.base': texts.BIRTHNAME_PATTERN,
            }),
            email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
                'string.empty': texts.SAISIR_VOTRE_ADRESSE_EMAIL,
                'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
            }),
            password: isCreatingSubscription
                ? Joi.any()
                : Joi.string().pattern(PASS_REGEX).required().messages({
                      'string.empty': texts.EMPTY_NEW_PASSWORD,
                      'string.pattern.base': texts.LE_MOT_DE_PASSE_DOIT_CONTENIR,
                  }),
            confirmPassword: Joi.when('password', {
                is: '',
                then: Joi.any(),
                otherwise: Joi.ref('password'),
            }).messages({ 'any.only': texts.PASSWORDS_MUST_MATCH }),
            corporateName: Joi.string()
                .required()
                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            corporateForm: Joi.string().required(),
            otherCorporateForm: Joi.when('corporateForm', {
                is: Joi.string().equal(corporateForms.other),
                then: Joi.string().required().messages({
                    'any.required': texts.SAISIR_LE_CHAMP,
                    'string.empty': texts.SAISIR_LE_CHAMP,
                }),
                otherwise: Joi.any(),
            }),
            conditions: Joi.object({
                termsAgreement: Joi.boolean().valid(true),
                acceptedEmails: Joi.boolean().valid(true),
            }),
        }),
        [isCreatingSubscription]
    );

    const resubmitSchema = useMemo(
        () => ({
            corporateName: schema.corporateName,
            corporateForm: schema.corporateForm,
            otherCorporateForm: schema.otherCorporateForm,
            firstName: schema.firstName,
            lastName: schema.lastName,
            birthName: schema.birthName,
            conditions: schema.conditions,
        }),
        [schema]
    );

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(isResubmit ? resubmitSchema : schema, form);
        setErrors(newErrors);
        return valid;
    }, [form, isResubmit, schema, resubmitSchema]);

    const handleChange = key => e => {
        const { value } = e.target;
        setForm(oldValue => ({ ...oldValue, [key]: value }));

        if (key === 'corporateForm' && value !== corporateForms.other) {
            setForm(oldValue => ({ ...oldValue, otherCorporateForm: '' }));
        }
        setEmailExists(false); // remove the error message on any field change
    };

    // returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return !isEqual(defaultForm, form);
    }, [form, defaultForm]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);
    const submitButtonText = useMemo(() => {
        if (changesMade) {
            return isResubmit || isAccountNotConfirmed ? texts.SAVE_AND_CONTINUE : texts.CREATE;
        } else {
            return isResubmit || isAccountNotConfirmed ? texts.SUIVANT : texts.CREATE;
        }
    }, [changesMade, isResubmit, isAccountNotConfirmed]);

    const handleConditionsChange = useCallback(e => {
        const { name, checked } = e.target;
        setForm(prevValue => ({
            ...prevValue,
            conditions: { ...prevValue.conditions, [name]: checked },
        }));
    }, []);

    const handleSubmit = useCallback(
        () => () => {
            if (!formIsValid()) return;
            if (isResubmit) {
                const { password, confirmPassword, ...rest } = form;
                signUp({
                    resubmit: true,
                    data: {
                        ...rest,
                        fundId: fundId + '',
                        fundName: currentInvestmentFund?.name,
                        roleId: roleId + '',
                        stepId: tunnelStep.step.id,
                        roleName,
                        label,
                        subcategoryId: subcategory,
                    },
                });
            } else {
                signUp({
                    resubmit: false,
                    data: {
                        ...form,
                        fundId: fundId + '',
                        fundName: currentInvestmentFund?.name,
                        roleId: roleId + '',
                        stepId: tunnelStep.step.id,
                        externalDistributorEmail,
                        roleName,
                        label,
                        subcategoryId: subcategory,
                        referralEmail: queryEmail,
                        sgpName: currentInvestmentFund.sgp?.name ?? '',
                        sgpLogo: currentInvestmentFund.sgp?.logo?.url ?? '',
                    },
                });
            }
        },
        [
            form,
            roleId,
            signUp,
            roleName,
            formIsValid,
            tunnelStep,
            fundId,
            label,
            subcategory,
            isResubmit,
            queryEmail,
            currentInvestmentFund,
        ]
    );

    useEffect(() => {
        if (isSuccess && (isResubmit || userStep || hasFinishedSubscription)) {
            reset();
            handleNextStep(true);
        }
    }, [isSuccess, isResubmit, handleNextStep, userStep, hasFinishedSubscription, reset]);

    useEffect(() => {
        if (isError) {
            if (error?.status === 409 && error?.data?.message === InternalErrors.USERNAME_EXISTS) {
                setEmailExists(true);
            }
        }
    }, [isError, error]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                handleSubmit,
                checkChanges,
                !!internalUser && !isCreatingSubscription
            ),
        [handleSubmit, checkChanges, isCreatingSubscription, internalUser, setTunnelChangesHandlers]
    );

    return (
        <React.Fragment>
            {!isSuccess && (
                <TunnelBox>
                    {!!Object.keys(tunnelErrors).length && (
                        <ErrorMessage gutterBottom message={texts.FORM_HAS_ERRORS} />
                    )}
                    <form onSubmit={e => e.preventDefault()}>
                        <Typography variant="h6" gutterBottom>
                            {texts.VOTRE_SOCIETE}
                        </Typography>
                        <CustomTextField
                            inputStyle={classes.field}
                            label={texts.DENOMINATION}
                            value={form.corporateName}
                            onChange={handleChange('corporateName')}
                            error={errors.corporateName || tunnelErrors.corporateName}
                            showError={!tunnelErrors.corporateName}
                        />
                        <CustomSelect
                            inputStyle={classes.field}
                            label={texts.FORME_SOCIALE}
                            value={form.corporateForm}
                            onChange={handleChange('corporateForm')}
                            error={errors.corporateForm || tunnelErrors.corporateForm}
                        >
                            {Object.values(corporateForms).map(corporateForm => (
                                <MenuItem key={corporateForm} value={corporateForm}>
                                    {corporateForm}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        {form.corporateForm === corporateForms.other && (
                            <CustomTextField
                                inputStyle={classes.field}
                                value={form.otherCorporateForm}
                                onChange={handleChange('otherCorporateForm')}
                                error={errors.otherCorporateForm}
                                showError={!!errors.otherCorporateForm}
                            />
                        )}
                        <Typography variant="h6" gutterBottom>
                            {texts.REPRESENTED_BY}
                        </Typography>
                        <BasicInfoForm
                            emailExists={emailExists}
                            form={form}
                            errors={errors}
                            handleChange={handleChange}
                            classes={classes}
                            tunnelErrors={tunnelErrors}
                            user={internalUser}
                        />
                        {!internalUser && (
                            <PasswordForm
                                classes={classes}
                                form={form}
                                errors={errors}
                                user={internalUser}
                                handleChange={handleChange}
                            />
                        )}
                        <ConditionsForm
                            conditions={form.conditions}
                            onChange={handleConditionsChange}
                        />
                    </form>
                    {errors['conditions,termsAgreement'] && (
                        <ErrorMessage
                            gutterBottom
                            message={texts.VOUS_DEVEZ_ACCEPTER_LES_CONDITIONS}
                        />
                    )}
                    {errors['conditions,acceptedEmails'] && (
                        <ErrorMessage
                            gutterBottom
                            message={texts.VOUS_DEVEZ_ACCEPTER_D_ETRE_CONTACTEE}
                        />
                    )}
                    {isSuccess && isResubmit && (
                        <Typography align="left" gutterBottom variant="body2">
                            {texts.YOUR_INFO_SAVED}
                        </Typography>
                    )}
                </TunnelBox>
            )}
            {isSuccess && !isCreatingSubscription && (
                <Typography align="center" variant="body2">
                    {texts.SHOULD_CLOSE_PAGE}
                </Typography>
            )}
            {!isSuccess && (
                <SubmitButton
                    disabled={isLoading}
                    text={submitButtonText}
                    loading={isLoading}
                    onClick={changesMade || !isStatusAcceptable ? handleSubmit() : handleNextStep}
                />
            )}
        </React.Fragment>
    );
};

PMForm.propTypes = {
    roleName: PropTypes.string,
    handleNextStep: PropTypes.func,
    userStep: PropTypes.object,
    tunnelStep: PropTypes.object,
};

export default PMForm;
